import { Card } from "@libertify/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useAntTheme } from "hooks/useAntTheme/";
import { SlideTemplatePreview } from "../../../storyboardEditor/components/Template/SlideTemplatePreview";
import { aspectRatioEnumToNumber } from "../../helpers";
import type { ThemeResponse } from "../../hooks/useThemeApi";
import { useThemePreviewSlides } from "./useThemePreviewSlides";

// Scrollable preview strip. Portrait themes scroll horizontally (tall slides
// side by side); landscape themes scroll vertically (wide slides stacked, a
// little smaller). Either way several slides are visible at once so it's clear
// the strip scrolls to reveal every slide type.
const AREA_HEIGHT = 240;
const PORTRAIT_PADDING = 20;
const PORTRAIT_SLIDE_HEIGHT = AREA_HEIGHT - PORTRAIT_PADDING * 2;
const LANDSCAPE_SLIDE_HEIGHT = 150;
const SLIDE_GAP = 12;

const noop = () => undefined;

type ThemeCardProps = {
  theme: ThemeResponse;
  onSelect: (theme: ThemeResponse) => void;
};

export const ThemeCard = ({ theme, onSelect }: ThemeCardProps) => {
  const { theme: antTheme } = useAntTheme();

  // Build the (non-trivial) previews only once the card scrolls into view, so
  // a gallery with many themes paints its shell immediately and fills in.
  const scrollerRef = useRef<HTMLDivElement>(null);
  // Without IntersectionObserver (e.g. non-browser/test env) build immediately.
  const [isVisible, setIsVisible] = useState(
    () => typeof IntersectionObserver === "undefined"
  );

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || isVisible) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      // Start building a little before the card reaches the viewport.
      { rootMargin: "200px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [isVisible]);

  const previews = useThemePreviewSlides(theme, isVisible);
  const ratio = aspectRatioEnumToNumber(theme.aspect_ratio);
  const isPortrait = ratio < 1;

  // Every slide of a theme shares its aspect ratio, so one size fits them all.
  const slideSize = useMemo(() => {
    const height = isPortrait ? PORTRAIT_SLIDE_HEIGHT : LANDSCAPE_SLIDE_HEIGHT;
    return { width: Math.round(height * ratio), height };
  }, [isPortrait, ratio]);

  return (
    <Card hoverable padding="none" onClick={() => onSelect(theme)}>
      <StyledScroller ref={scrollerRef} $portrait={isPortrait}>
        {previews.map((preview) => (
          <SlideTemplatePreview
            key={preview.slideType}
            templateSize={slideSize}
            // Flat previews: square corners, thin grey outline, no hover recolor
            // (the explicit borderColor wins over HoverableCard's hover).
            border={{
              borderWidth: 1,
              borderRadius: 0,
              borderColor: antTheme.colorGrey,
            }}
            opacity={1}
            onCardClick={noop}
            slide={preview.slide}
            template={preview.template}
            marginBlockEnd={0}
          />
        ))}
      </StyledScroller>

      <StyledFooter>
        <StyledName>{theme.name}</StyledName>
        {theme.description && (
          <StyledDescription>{theme.description}</StyledDescription>
        )}
      </StyledFooter>
    </Card>
  );
};

// ─── Styled components ───

const StyledScroller = styled.div<{ $portrait: boolean }>`
  display: flex;
  flex-direction: ${({ $portrait }) => ($portrait ? "row" : "column")};
  align-items: center;
  gap: ${SLIDE_GAP}px;
  height: ${AREA_HEIGHT}px;
  background: ${({ theme }) => theme.colorFillTertiary};
  border-bottom: 1px solid ${({ theme }) => theme.colorBorderSecondary};

  ${({ $portrait }) =>
    $portrait
      ? `
        overflow-x: auto;
        overflow-y: hidden;
        padding: ${PORTRAIT_PADDING}px;
      `
      : `
        overflow-x: hidden;
        overflow-y: auto;
        padding-block: 16px;
      `}

  /* Keep every slide at its intended size instead of shrinking to fit. */
  > * {
    flex: 0 0 auto;
  }

  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colorTextQuaternary} transparent;
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colorTextQuaternary};
    border-radius: 3px;
  }
`;

const StyledFooter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 16px;
`;

const StyledName = styled.span`
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme }) => theme.colorText};
`;

const StyledDescription = styled.span`
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colorTextTertiary};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;
